<script setup>
/**
 * AppHomePreview — a faithful recreation of the mobile app "Compete" tab
 * (Flutter mockup: redesign/style-a/compete.html), rebuilt as live markup
 * mapped to the app theme tokens. Rendered at the mockup's native 390px width
 * inside AppCallout's phone frame (which scales it to fit). Static/inert.
 */
const leagues = [
  {t: 'Mens League', s: 'Spring 2026 · 18 teams', chip: 'You: 2nd'},
  {t: 'Womens League', s: 'Spring 2026 · 12 teams', chip: null},
];
const ladders = [
  {t: 'Singles Ladder', s: '24 players · 1 challenge waiting', chip: 'You: 5th'},
  {t: 'Box Ladder', s: '16 players', chip: 'You: 3rd'},
];
const tournaments = [
  {t: 'Spring Open 2026', s: 'Groups + knockout · 32 teams', chip: 'Entered'},
  {t: 'Winter Cup 2025', s: 'Knockout · Finished', chip: null},
];
const clubActions = [
  {ic: 'mdi-calendar-month', lb: 'Matches'},
  {ic: 'mdi-chat-outline', lb: 'Start chat'},
  {ic: 'mdi-share-variant', lb: 'Share'},
  {ic: 'mdi-chart-bar', lb: 'Stats'},
  {ic: 'mdi-account-group', lb: 'Members'},
];
</script>

<template>
  <div class="cp">
    <!-- status bar -->
    <div class="cp-status">
      <span>9:41</span>
      <span class="cp-status-ic">
        <v-icon size="14">mdi-signal-cellular-3</v-icon>
        <v-icon size="14">mdi-wifi</v-icon>
        <v-icon size="16">mdi-battery</v-icon>
      </span>
    </div>

    <!-- app bar -->
    <div class="cp-appbar">
      <div class="cp-titles">
        <div class="cp-hello">Your competitions</div>
        <div class="cp-name">Compete</div>
      </div>
      <div class="cp-action">
        <v-icon size="20">mdi-chat-outline</v-icon>
        <span class="cp-badge">3</span>
      </div>
      <div class="cp-action"><v-icon size="20">mdi-magnify</v-icon></div>
    </div>

    <!-- scrolling content -->
    <div class="cp-content">

      <!-- selected club + actions -->
      <div class="cp-card cp-club">
        <div class="cp-club-top">
          <div class="cp-crest">RS</div>
          <div class="cp-who">
            <div class="cp-t">Riverside Club</div>
            <div class="cp-s">86 members · You're a member &amp; admin</div>
          </div>
          <v-icon size="20" color="textSubtle">mdi-unfold-more-horizontal</v-icon>
        </div>
        <div class="cp-club-actions">
          <div v-for="a in clubActions" :key="a.lb" class="cp-act">
            <div class="cp-act-ic"><v-icon size="20">{{ a.ic }}</v-icon></div>
            <div class="cp-act-lb">{{ a.lb }}</div>
          </div>
        </div>
        <div class="cp-manage">
          <v-icon size="18" color="primary">mdi-shield-account</v-icon>
          <span class="cp-manage-lb">Manage club</span>
          <span class="cp-chip cp-chip--brand">2 requests</span>
          <v-icon size="18" color="textSubtle">mdi-chevron-right</v-icon>
        </div>
      </div>

      <!-- Leagues -->
      <div class="cp-section-head"><h2>Box leagues</h2></div>
      <div class="cp-card cp-list">
        <div v-for="row in leagues" :key="row.t" class="cp-row">
          <div class="cp-tile cp-tile--league"><v-icon size="21">mdi-table</v-icon></div>
          <div class="cp-who">
            <div class="cp-t">{{ row.t }}</div>
            <div class="cp-s">{{ row.s }}</div>
          </div>
          <div class="cp-meta">
            <span v-if="row.chip" class="cp-chip cp-chip--brand">{{ row.chip }}</span>
            <v-icon size="20" color="textSubtle">mdi-chevron-right</v-icon>
          </div>
        </div>
      </div>

      <!-- Ladders -->
      <div class="cp-section-head"><h2>Ladders</h2></div>
      <div class="cp-card cp-list">
        <div v-for="row in ladders" :key="row.t" class="cp-row">
          <div class="cp-tile cp-tile--ladder"><v-icon size="21">mdi-ladder</v-icon></div>
          <div class="cp-who">
            <div class="cp-t">{{ row.t }}</div>
            <div class="cp-s">{{ row.s }}</div>
          </div>
          <div class="cp-meta">
            <span v-if="row.chip" class="cp-chip cp-chip--brand">{{ row.chip }}</span>
            <v-icon size="20" color="textSubtle">mdi-chevron-right</v-icon>
          </div>
        </div>
      </div>

      <!-- Tournaments -->
      <div class="cp-section-head"><h2>Tournaments</h2></div>
      <div class="cp-card cp-list">
        <div v-for="row in tournaments" :key="row.t" class="cp-row">
          <div class="cp-tile cp-tile--tourn"><v-icon size="21">mdi-trophy</v-icon></div>
          <div class="cp-who">
            <div class="cp-t">{{ row.t }}</div>
            <div class="cp-s">{{ row.s }}</div>
          </div>
          <div class="cp-meta">
            <span v-if="row.chip" class="cp-chip cp-chip--ok">{{ row.chip }}</span>
            <v-icon size="20" color="textSubtle">mdi-chevron-right</v-icon>
          </div>
        </div>
      </div>
    </div>

    <!-- bottom nav -->
    <nav class="cp-nav">
      <span class="cp-nav-item">
        <span class="cp-nav-pill"><v-icon size="22">mdi-home-outline</v-icon></span>Home
      </span>
      <span class="cp-nav-item cp-nav-item--active">
        <span class="cp-nav-pill"><v-icon size="22">mdi-trophy</v-icon></span>Compete
      </span>
      <span class="cp-nav-item">
        <span class="cp-nav-pill"><v-icon size="22">mdi-account-outline</v-icon></span>Profile
      </span>
    </nav>
  </div>
</template>

<style scoped lang="scss">
.cp {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: rgb(var(--v-theme-light));
  color: rgb(var(--v-theme-text));
  font-size: 14px;
}

/* status bar */
.cp-status {
  height: 50px;
  flex-shrink: 0;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 0 28px 6px;
  font-size: 14px;
  font-weight: 600;
}

.cp-status-ic {
  display: flex;
  align-items: center;
  gap: 5px;
}

/* app bar */
.cp-appbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 20px 14px;
  flex-shrink: 0;
}

.cp-titles {
  flex: 1;
  min-width: 0;
}

.cp-hello {
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
  font-weight: 500;
}

.cp-name {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.2px;
}

.cp-action {
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  display: flex;
  align-items: center;
  justify-content: center;
}

.cp-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 17px;
  height: 17px;
  border-radius: 9px;
  background: rgb(var(--v-theme-primary));
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  border: 2px solid rgb(var(--v-theme-light));
}

/* content */
.cp-content {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 0 20px;
}

.cp-card {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 14px;
  overflow: hidden;
}

.cp-section-head {
  margin: 22px 0 12px;

  h2 {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: -0.2px;
  }
}

/* chips */
.cp-chip {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;

  &--brand {
    background: rgb(var(--v-theme-brandTint));
    color: rgb(var(--v-theme-primary));
  }

  &--ok {
    background: rgb(var(--v-theme-successTint));
    color: rgb(var(--v-theme-success));
  }
}

/* club card */
.cp-club {
  padding: 16px;
}

.cp-club-top {
  display: flex;
  align-items: center;
  gap: 12px;
}

.cp-crest {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 800;
  flex-shrink: 0;
}

.cp-who {
  flex: 1;
  min-width: 0;
}

.cp-t {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cp-s {
  font-size: 11.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cp-club-actions {
  display: flex;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}

.cp-act {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.cp-act-ic {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgb(var(--v-theme-light));
  border: 1px solid rgb(var(--v-theme-borderLight));
  display: flex;
  align-items: center;
  justify-content: center;
}

.cp-act-lb {
  font-size: 10.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-charcoal));
}

.cp-manage {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  padding: 11px 14px;
  background: rgb(var(--v-theme-light));
  border-radius: 12px;
}

.cp-manage-lb {
  flex: 1;
  font-size: 12.5px;
  font-weight: 600;
}

/* comp rows */
.cp-list {
  padding: 2px 0;
}

.cp-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 14px;

  & + .cp-row {
    border-top: 1px solid rgb(var(--v-theme-borderLight));
  }
}

.cp-tile {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &--league {
    background: rgb(var(--v-theme-brandTint));
    color: rgb(var(--v-theme-primary));
  }

  &--ladder {
    background: #E8F1FB;
    color: #2477C8;
  }

  &--tourn {
    background: rgb(var(--v-theme-warningTint));
    color: rgb(var(--v-theme-warning));
  }
}

.cp-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

/* bottom nav */
.cp-nav {
  flex-shrink: 0;
  display: flex;
  padding: 10px 8px 20px;
  background: rgb(var(--v-theme-surface));
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}

.cp-nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  color: rgb(var(--v-theme-textSubtle));
  font-size: 10.5px;
  font-weight: 600;

  &--active {
    color: rgb(var(--v-theme-primary));
  }
}

.cp-nav-pill {
  width: 56px;
  height: 30px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cp-nav-item--active .cp-nav-pill {
  background: rgb(var(--v-theme-brandTint));
}
</style>
